import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import CategoryNav from '../components/CategoryNav';
import BannerCarousel from '../components/BannerCarousel';
import ProductCard from '../components/ProductCard';
import Cart from '../components/Cart';
import ProductModal from '../components/ProductModal';
import { useCart } from '../context/CartContext';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [destaquesProducts, setDestaquesProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { getItemCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const categoriesRes = await axios.get(`${API}/categories?ativo=true`);
      const cats = categoriesRes.data;
      setCategories(cats);

      const destaquesRes = await axios.get(`${API}/products?destaque=true&limit=6`);
      setDestaquesProducts(destaquesRes.data);

      const productsData = {};
      for (const cat of cats) {
        const prodRes = await axios.get(`${API}/products?categoria=${encodeURIComponent(cat.nome)}&limit=6`);
        productsData[cat.slug] = prodRes.data;
      }
      setProductsByCategory(productsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleAddToCart = () => {
    toast.success(`${selectedProduct.nome} adicionado ao carrinho!`, {
      duration: 3000,
      action: {
        label: 'Ver Carrinho',
        onClick: () => {
          setShowCart(true);
          toast.dismiss();
        }
      }
    });
  };

  // Componente de Seção de Produtos com Rolagem Horizontal
  const ProductSection = ({ title, products, categorySlug, isDark = false }) => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    useEffect(() => {
      checkScroll();
      const ref = scrollRef.current;
      if (ref) {
        ref.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
          ref.removeEventListener('scroll', checkScroll);
          window.removeEventListener('resize', checkScroll);
        };
      }
    }, [products]);

    const scroll = (direction) => {
      if (scrollRef.current) {
        const scrollAmount = 280;
        scrollRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      }
    };

    if (products.length === 0) return null;

    return (
      <section style={{ 
        padding: '2rem 0', 
        backgroundColor: isDark ? '#000' : '#fff'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          {/* Header da seção */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '1rem'
          }}>
            <h2 style={{ 
              fontFamily: 'Playfair Display, serif', 
              fontSize: '1.5rem', 
              margin: 0, 
              color: isDark ? '#E8DCC4' : '#000' 
            }}>
              {title}
            </h2>
            <button
              onClick={() => navigate(`/categoria/${categorySlug}`)}
              style={{
                backgroundColor: '#000',
                color: '#fff',
                border: 'none',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              data-testid={`view-category-${categorySlug}`}
            >
              Ver mais
              <ArrowRight size={14} />
            </button>
          </div>

          {/* Container de produtos com scroll */}
          <div style={{ position: 'relative' }}>
            {/* Botão scroll esquerda */}
            {canScrollLeft && (
              <button
                onClick={() => scroll('left')}
                style={{
                  position: 'absolute',
                  left: '-0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                data-testid={`scroll-left-${categorySlug}`}
              >
                <ChevronLeft size={20} />
              </button>
            )}

            {/* Lista de produtos */}
            <div
              ref={scrollRef}
              style={{
                display: 'flex',
                gap: '1rem',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: '0.5rem'
              }}
              className="hide-scrollbar"
            >
              {products.map(product => (
                <div 
                  key={product.id} 
                  onClick={() => handleProductClick(product)}
                  style={{
                    flexShrink: 0,
                    width: '160px',
                    scrollSnapAlign: 'start'
                  }}
                >
                  <ProductCard product={product} compact />
                </div>
              ))}
              
              {/* Card "Ver mais" no final */}
              {products.length >= 6 && (
                <div
                  onClick={() => navigate(`/categoria/${categorySlug}`)}
                  style={{
                    flexShrink: 0,
                    width: '120px',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    scrollSnapAlign: 'start',
                    border: isDark ? '1px solid #333' : '1px solid #e0e0e0'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? '#2a2a2a' : '#e8e8e8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? '#1a1a1a' : '#f5f5f5';
                  }}
                  data-testid={`view-more-${categorySlug}`}
                >
                  <ArrowRight size={28} style={{ color: isDark ? '#E8DCC4' : '#666', marginBottom: '0.5rem' }} />
                  <p style={{ 
                    fontSize: '0.75rem', 
                    textAlign: 'center', 
                    color: isDark ? '#E8DCC4' : '#666',
                    padding: '0 0.5rem'
                  }}>
                    Ver todos
                  </p>
                </div>
              )}
            </div>

            {/* Botão scroll direita */}
            {canScrollRight && (
              <button
                onClick={() => scroll('right')}
                style={{
                  position: 'absolute',
                  right: '-0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                data-testid={`scroll-right-${categorySlug}`}
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FAFAFA' }}>
        <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem' }}>Carregando...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <Header 
        onSearch={() => {}} 
        cartCount={getItemCount()} 
        onCartClick={() => setShowCart(true)}
      />

      <CategoryNav />

      <BannerCarousel />

      {/* Seção Destaques */}
      {destaquesProducts.length > 0 && (
        <ProductSection 
          title="Destaques" 
          products={destaquesProducts} 
          categorySlug="destaques"
        />
      )}

      {/* Seções por Categoria */}
      {categories.map((category) => {
        const products = productsByCategory[category.slug] || [];
        const isDark = category.slug === 'importados';
        
        return (
          <ProductSection
            key={category.id}
            title={category.nome}
            products={products}
            categorySlug={category.slug}
            isDark={isDark}
          />
        );
      })}

      <footer className="bg-black text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/logo-margareth-full-white.png"
              alt="Margareth Bernardo"
              className="h-24 md:h-32 object-contain"
            />
          </div>
          <p className="mb-4" style={{ color: '#E8DCC4' }}>Perfumaria de Qualidade</p>
          <div className="flex justify-center items-center space-x-4">
            <a 
              href="https://instagram.com/marg_bernardoo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#E8DCC4' }}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <span style={{ color: '#E8DCC4' }}>@marg_bernardoo</span>
          </div>
          <p className="mt-6 text-sm" style={{ color: '#888' }}>WhatsApp: (62) 99848-5292</p>
        </div>
      </footer>

      <Cart open={showCart} onClose={() => setShowCart(false)} />
      
      <ProductModal
        product={selectedProduct}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAddToCart={handleAddToCart}
      />

      {/* CSS para esconder scrollbar */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Home;
